/**
 * Bilan AMO — ÉCRAN DE CONTRÔLE (#460 §1, #464).
 *
 * Porte de validation du rapport officiel ANAH/DRIHL : JJ vérifie l'identité, le
 * périmètre, la période et les chiffres, complète ce qui manque, puis génère.
 *
 * ⚠️ « À CONFIRMER » vit ICI et nulle part ailleurs. Le PDF remis à
 * l'administration n'affiche jamais nos incertitudes internes : quand une date
 * n'est pas établie, il écrit « modification intervenue depuis le précédent
 * bilan ». Cet écran est donc le seul endroit où les manques sont visibles.
 *
 * ⛔ Aucun indicateur de qualité d'instruction ici : ils attendent la
 * qualification de CC AUDIT (3 087 « contrôles » ramenés à 451 réels).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, CheckCircle2, FileDown, Loader2, RefreshCw } from "lucide-react";
import ChampDate from "../../components/ui/champ-date";

const API = process.env.REACT_APP_BACKEND_URL || "";

/** Champs que JJ complète : ce que l'ANAH détient encore, absent d'OmniHub.
 *
 * ⚠️ Les champs historiques manquants restent SAISISSABLES ici — c'est le seul
 * endroit où un manque se comble. Laissé vide, aucun n'est deviné : le rapport
 * formule autrement plutôt que d'inventer (#475).
 */
const CHAMPS_SAISIE = [
  {
    cle: "reference_habilitation",
    label: "Référence d'enregistrement au ROD (si l'ANAH la communique)",
    aide: "Vide, le rapport en sollicite la confirmation — aucune référence n'est inventée.",
  },
  { cle: "ancienne_adresse", label: "Ancienne adresse connue de l'ANAH", exemple: "Clichy" },
  { cle: "date_effet_adresse", label: "Date d'effet du changement d'adresse", type: "date" },
  { cle: "justificatif_adresse", label: "Justificatif (référence)" },
  // PRÉSIDENCE — exercée par une PERSONNE MORALE. ⛔ L'ancien président n'est
  // ni saisi ni publié : le changement se mentionne sans nommer qui partait.
  { cle: "president_personne_morale", label: "Présidence — dénomination", exemple: "SOCIÉTÉ EXEMPLE",
    aide: "Obligatoire : le référentiel porte une valeur périmée et ne sait pas exprimer cette structure." },
  { cle: "president_personne_morale_forme", label: "Présidence — forme juridique", exemple: "SAS" },
  { cle: "president_personne_morale_siren", label: "Présidence — SIREN", exemple: "000 000 000" },
  { cle: "president_personne_morale_representant", label: "Présidence — représentée par",
    exemple: "NOM EXEMPLE" },
  { cle: "president_personne_morale_representant_fonction",
    label: "Présidence — fonction du représentant", exemple: "Président" },
  { cle: "date_effet_presidence", label: "Date d'effet du changement de présidence", type: "date",
    aide: "Non requise : le Kbis joint fait foi. Publiée si vous la renseignez." },
  { cle: "justificatif_presidence", label: "Justificatif (référence)" },
  // 🔴 #786 — le courrier DRIHL dit « effectifs », ⛔ jamais « ETP ». Le libellé
  // imposait une conversion que l'administration ne demande pas.
  { cle: "effectifs_actuels", label: "Effectif actuel de la structure",
    aide: "Nombre de personnes composant actuellement l'effectif. " +
          "Ne pas convertir en ETP sauf demande explicite." },
  // ⚠️ Formulation LIBRE et établie — ⛔ ne rien inventer : « aucun recrutement
  // prévu » est une réponse, au même titre qu'un nombre.
  { cle: "previsionnel_recrutement", label: "Prévisionnel de recrutement",
    aide: "Formulation libre : « aucun recrutement prévu », ou nombre et profil, " +
          "avec l'horizon s'il est connu." },
  // ⚠️ Saisie d'écran — ⛔ la DRIHL ne l'exige PAS.
  { cle: "capacite_mensuelle", label: "Capacité de traitement (demandes/mois)",
    aide: "Non exigé par la DRIHL — à renseigner seulement si vous le souhaitez." },
];

const Carte = ({ titre, children, action }) => (
  <section
    style={{
      background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
      borderRadius: 12, padding: 16, marginBottom: 16,
    }}
  >
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
      <h2 style={{ fontSize: 15, fontWeight: 500, color: "var(--oh-text)", margin: 0 }}>{titre}</h2>
      {action}
    </div>
    {children}
  </section>
);

const Ligne = ({ label, valeur, alerte }) => (
  <div style={{ display: "flex", gap: 12, padding: "6px 0", borderBottom: "0.5px solid var(--oh-border)" }}>
    <span style={{ flex: "0 0 240px", fontSize: 13, color: "var(--oh-text-secondary)" }}>{label}</span>
    <span style={{ fontSize: 13, color: alerte ? "var(--oh-stat-amber-text)" : "var(--oh-text)" }}>
      {valeur ?? "—"}
    </span>
  </div>
);

const Encart = ({ ton, titre, items }) => {
  if (!items?.length) return null;
  return (
    <div
      style={{
        background: `var(--oh-stat-${ton}-bg)`, color: `var(--oh-stat-${ton}-text)`,
        borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, lineHeight: 1.6,
      }}
    >
      <strong style={{ fontWeight: 500 }}>{titre}</strong>
      <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
        {items.map((t, i) => <li key={i}>{t}</li>)}
      </ul>
    </div>
  );
};

export default function BilanAMOControle() {
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [saisies, setSaisies] = useState({});
  const [editorial, setEditorial] = useState({ synthese_executive: "", conclusion: "" });
  const [generation, setGeneration] = useState(false);
  // 🔴 #709 — LA DATE D'ARRÊT EST UN CHOIX VISIBLE, ⛔ JAMAIS `now()` IMPLICITE.
  // L'écran ne l'envoyait pas : le serveur retombait sur le jour de génération,
  // et le même bilan régénéré demain ne portait plus les mêmes chiffres — la
  // production continue de travailler. Le bilan officiel porte 2026-08-13 ;
  // générer « aujourd'hui » produirait un AUTRE document.
  // ⛔ Aucune valeur par défaut : tant qu'elle est vide, la génération est refusée.
  const [arreteAu, setArreteAu] = useState("");

  const charger = useCallback(async () => {
    setChargement(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/rapports/bilan-amo/controle`, { credentials: "include" });
      if (!r.ok) throw new Error(`Chargement impossible (${r.status})`);
      setDonnees(await r.json());
    } catch (e) { setErreur(e.message); }
    finally { setChargement(false); }
  }, []);

  useEffect(() => { charger(); }, [charger]);

  // 🔴 #724 — LES TEXTES OFFICIELS VIENNENT DU PRODUIT, ⛔ pas de la mémoire de JJ.
  // Ils vivaient dans un script : l'écran partait avec des champs VIDES, et le
  // PDF produit ici aurait porté d'autres textes que le document remis à
  // l'administration. ⚠️ Ils suivent `arrete_au` — la synthèse cite la date en
  // toutes lettres — donc on les recharge quand la date change.
  // ⚠️ Ce qui a été SAISI n'est jamais écrasé : on ne remplit que le vide.
  useEffect(() => {
    if (!arreteAu) return;
    let annule = false;
    (async () => {
      try {
        const r = await fetch(
          `${API}/api/rapports/bilan-amo/textes-officiels?arrete_au=${arreteAu}`,
          { credentials: "include" });
        if (!r.ok) return;
        const { textes } = await r.json();
        if (annule) return;
        setEditorial((actuel) => ({
          synthese_executive: actuel.synthese_executive?.trim()
            ? actuel.synthese_executive : textes.synthese_executive,
          conclusion: actuel.conclusion?.trim()
            ? actuel.conclusion : textes.conclusion,
        }));
      } catch { /* ⚠️ un texte non chargé laisse les champs tels quels */ }
    })();
    return () => { annule = true; };
  }, [arreteAu]);

  const genererPdf = async () => {
    setGeneration(true); setErreur(null);
    try {
      const r = await fetch(`${API}/api/rapports/bilan-amo/pdf`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        // ⛔ Les textes partent SANS retouche : ni découpe, ni normalisation.
        // Le serveur ne les tronque pas non plus (#470 §6).
        // ⚠️ `deposer: true` — le PDF n'est plus seulement téléchargé : il est
        // déposé en bibliothèque et SATISFAIT l'exigence `hab_rapport_activite`.
        // Sans lui, JJ pouvait générer dix fois, l'exigence restait manquante.
        body: JSON.stringify({ saisies, ...editorial,
                               arrete_au: arreteAu, deposer: true }),
      });
      if (!r.ok) throw new Error(`Génération impossible (${r.status})`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "bilan-amo.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErreur(e.message); }
    finally { setGeneration(false); }
  };

  const bloquants = donnees?.bloquants || [];
  // Exigences déclarées par le serveur, vérifiées contre CE QUI EST SAISI ici :
  // l'écran est chargé avant toute saisie, un bloquant calculé au chargement
  // resterait bloquant pour toujours. Le POST les réexige côté serveur.
  const manquants = useMemo(
    () => Object.entries(donnees?.saisies_requises || {})
      .filter(([cle]) => !String(saisies[cle] || "").trim())
      .map(([, libelle]) => libelle),
    [donnees, saisies]);
  const peutFiger = useMemo(
    () => Boolean(donnees) && bloquants.length === 0 && manquants.length === 0,
    [donnees, bloquants, manquants]);

  if (chargement) {
    return (
      <div style={{ padding: 24, color: "var(--oh-text-secondary)", display: "flex", gap: 8 }}>
        <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Chargement du contrôle…
      </div>
    );
  }
  if (erreur && !donnees) {
    return <div style={{ padding: 24, color: "var(--oh-stat-red-text)" }}>{erreur}</div>;
  }

  const { identite, perimetre, periode, donnees: d, evolutions_administratives: evo } = donnees;

  return (
    <div style={{ padding: 24, maxWidth: 1080, margin: "0 auto", color: "var(--oh-text)" }}>
      <header style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, fontWeight: 500, margin: 0 }}>
          Bilan d'activité AMO — contrôle avant émission
        </h1>
        <p style={{ fontSize: 13, color: "var(--oh-text-secondary)", marginTop: 4, lineHeight: 1.6 }}>
          Vérifiez l'identité, le périmètre et les chiffres, complétez ce qui manque,
          puis générez le rapport. Les mentions « à confirmer » restent sur cet écran
          et n'apparaissent jamais dans le document remis.
        </p>
      </header>

      <Encart ton="red" titre="À résoudre avant toute génération"
              items={[...bloquants, ...manquants]} />
      <Encart ton="amber" titre="Points de vigilance — le rapport peut être produit sans, mais vérifiez"
              items={donnees.points_de_vigilance} />

      <Carte titre="Identité de la structure"
             action={<button onClick={charger} aria-label="Recharger les données"
                             style={{ border: "1px solid var(--oh-border)", background: "transparent",
                                      color: "var(--oh-text-secondary)", borderRadius: 8,
                                      padding: "6px 10px", fontSize: 13, display: "flex", gap: 6 }}>
                       <RefreshCw size={14} aria-hidden="true" /> Recharger
                     </button>}>
        <Ligne label="Raison sociale" valeur={identite.raison_sociale} />
        <Ligne label="Adresse actuelle" valeur={identite.adresse_actuelle} />
        <Ligne label="Représentant au référentiel (PÉRIMÉ)"
               valeur={identite.representant_referentiel} alerte />
        <Ligne label="Présidence publiée dans le rapport"
               valeur={identite.presidence_publiee
                 ? "structure saisie ci-dessous"
                 : "non établie — le rapport ne sera pas généré"}
               alerte={!identite.presidence_publiee} />
        <Ligne label="Fonction du représentant" valeur={identite.fonction_representant} />
        <Ligne label="Référence de l'habilitation"
               valeur={identite.reference_habilitation || identite.reference_habilitation_origine}
               alerte={!identite.reference_habilitation} />
        {identite.reference_habilitation_texte_rapport && (
          <p style={{ fontSize: 12, color: "var(--oh-text-secondary)", marginTop: 10, lineHeight: 1.6 }}>
            Formulation retenue, reprise telle quelle dans le rapport :
            «&nbsp;{identite.reference_habilitation_texte_rapport}&nbsp;»
          </p>
        )}
      </Carte>

      <Carte titre="Évolutions administratives à porter à la connaissance de l'ANAH">
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--oh-text-secondary)" }}>
              <th style={{ padding: "6px 0", fontWeight: 500 }}>Objet</th>
              <th style={{ padding: "6px 0", fontWeight: 500 }}>Détenu par l'ANAH</th>
              <th style={{ padding: "6px 0", fontWeight: 500 }}>Situation actuelle</th>
            </tr>
          </thead>
          <tbody>
            {evo.map((l) => (
              <tr key={l.objet} style={{ borderTop: "0.5px solid var(--oh-border)" }}>
                <td style={{ padding: "8px 0" }}>{l.objet}</td>
                <td style={{ padding: "8px 0", color: l.a_verifier ? "var(--oh-stat-amber-text)" : "var(--oh-text)" }}>
                  {l.ancien}
                </td>
                <td style={{ padding: "8px 0" }}>{l.nouveau}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p style={{ fontSize: 12, color: "var(--oh-text-secondary)", marginTop: 10, lineHeight: 1.6 }}>
          {donnees.note_historique}
        </p>
      </Carte>

      <Carte titre="Périmètre d'habilitation">
        <Ligne label="Départements habilités" valeur={perimetre.habilites.join(" · ")} />
        <Ligne label="Non habilités (demande d'extension)"
               valeur={perimetre.non_habilites_demande_extension.join(" · ")} />
        <Ligne label="Source" valeur={perimetre.source} />
        {d?.anomalies_perimetre?.length > 0 && (
          <Encart ton="amber" titre="Écarts constatés"
                  items={d.anomalies_perimetre.map(
                    (a) => `Dép. ${a.departement} — ${a.type} : ${a.dossiers} dossier(s). ${a.consequence}`)} />
        )}
      </Carte>

      <Carte titre="Période et données">
        {/* 🔴 LE PARAMÈTRE LE PLUS LOURD DE CONSÉQUENCES, montré AVANT le bouton. */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
                      padding: "8px 0", borderBottom: "0.5px solid var(--oh-border)" }}>
          <label htmlFor="arrete-au" style={{ fontSize: 13, color: "var(--oh-text-secondary)",
                                              minWidth: 200 }}>
            Données arrêtées au
          </label>
          <ChampDate
            id="arrete-au"  value={arreteAu}
            onChange={(e) => setArreteAu(e.target.value)}
            data-testid="bilan-arrete-au"
            style={{ height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13,
                     background: "var(--oh-bg-surface)", color: "var(--oh-text)",
                     border: "1px solid var(--oh-border)" }} />
          <span style={{ fontSize: 12, color: "var(--oh-text-muted)" }}>
            Tout ce qui est postérieur est exclu, et cette date est imprimée dans le
            document. Le bilan officiel porte le 13/08/2026.
          </span>
        </div>
        <Ligne label="Exercice complet" valeur={periode.annee_pleine} />
        <Ligne label="Exercice en cours" valeur={`${periode.annee_en_cours} — arrêté au jour de génération`} />
        <Ligne label="Axe temporel" valeur={`${periode.axe} (jamais la date de création en base)`} />
        {d?.populations && (
          <>
            <Ligne label="Dossiers suivis" valeur={d.populations.base_totale_tenant} />
            <Ligne label="Rattachables à un exercice" valeur={d.populations.exploitable_sur_l_axe} />
            <Ligne label="Exclus (sans date de dépôt)"
                   valeur={`${d.populations.exclue_sans_date_depot} (${d.populations.part_exclue_pct} %)`}
                   alerte />
          </>
        )}
        {d?.completude?.moyenne && (
          <Ligne label="Complétude documentaire"
                 valeur={`${d.completude.moyenne.valeur} % — recalculée le ${d.completude.recalcule_le}`} />
        )}
        {d?.dossiers_par_departement && (
          <Ligne label="Dossiers déposés par département"
                 valeur={Object.entries(d.dossiers_par_departement)
                   .map(([dep, n]) => `${dep} : ${n}`).join("  ·  ")} />
        )}
      </Carte>

      <Carte titre="Éléments à compléter avant émission">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 12 }}>
          {CHAMPS_SAISIE.map((c) => (
            <label key={c.cle} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>{c.label}</span>
              <input
                type={c.type || "text"}
                value={saisies[c.cle] || ""}
                placeholder={c.exemple ? `ex. ${c.exemple}` : "Laisser vide si non établi"}
                onChange={(e) => setSaisies((p) => ({ ...p, [c.cle]: e.target.value }))}
                style={{
                  height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13,
                  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
                  border: "1px solid var(--oh-border)",
                }}
              />
              {c.aide && (
                <span style={{ fontSize: 11, color: "var(--oh-text-muted)", lineHeight: 1.6 }}>
                  {c.aide}
                </span>
              )}
            </label>
          ))}
        </div>
        <p style={{ fontSize: 12, color: "var(--oh-text-secondary)", marginTop: 10, lineHeight: 1.6 }}>
          Un champ laissé vide n'est jamais comblé par une valeur plausible. Si une date
          n'est pas établie, le rapport indique que la modification est intervenue depuis
          le précédent bilan.
        </p>
      </Carte>

      <Carte titre="Textes éditoriaux">
        <p style={{ fontSize: 12, color: "var(--oh-text-secondary)", margin: "0 0 12px", lineHeight: 1.6 }}>
          Collez ici les textes rédigés. Ils sont repris intégralement, sans
          retouche ni troncature. Laissés vides, le rapport affiche un point
          d'insertion visible — il n'invente aucun contenu.
        </p>
        <div style={{ display: "grid", gap: 12 }}>
          {[
            { cle: "synthese_executive", label: "Synthèse exécutive",
              aide: "Placée en tête du rapport, avant tout chiffre." },
            { cle: "conclusion", label: "Conclusion — renouvellement et extensions",
              aide: "Les trois demandes sont ajoutées automatiquement à sa suite." },
          ].map((c) => (
            <label key={c.cle} style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ fontSize: 12, color: "var(--oh-text-secondary)" }}>{c.label}</span>
              <textarea
                value={editorial[c.cle]}
                placeholder="Laisser vide tant que le texte n'est pas arrêté"
                onChange={(e) => setEditorial((p) => ({ ...p, [c.cle]: e.target.value }))}
                rows={6}
                style={{
                  borderRadius: 8, padding: 10, fontSize: 13, lineHeight: 1.6,
                  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
                  border: "1px solid var(--oh-border)", resize: "vertical",
                  fontFamily: "inherit",
                }}
              />
              <span style={{ fontSize: 11, color: "var(--oh-text-muted)", lineHeight: 1.6 }}>
                {c.aide}
              </span>
            </label>
          ))}
        </div>
      </Carte>

      {erreur && (
        <div style={{ background: "var(--oh-stat-red-bg)", color: "var(--oh-stat-red-text)",
                      borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13 }}>
          {erreur}
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={genererPdf}
          disabled={!peutFiger || generation || !arreteAu}
          data-testid="generer-bilan-amo"
          style={{
            display: "flex", alignItems: "center", gap: 8, height: 36, padding: "0 16px",
            borderRadius: 8, border: "none", fontSize: 13,
            background: peutFiger && arreteAu ? "var(--oh-accent-solid)" : "var(--oh-bg-surface)",
            color: peutFiger && arreteAu ? "var(--oh-accent-contrast)" : "var(--oh-text-muted)",
            cursor: peutFiger && arreteAu && !generation ? "pointer" : "not-allowed",
          }}
        >
          {generation
            ? <><Loader2 size={15} className="animate-spin" aria-hidden="true" /> Génération…</>
            : <><FileDown size={15} aria-hidden="true" /> Générer le rapport</>}
        </button>
        <span style={{ fontSize: 12, color: "var(--oh-text-secondary)", display: "flex", gap: 6 }}>
          {!arreteAu
            ? <><AlertTriangle size={14} aria-hidden="true" /> Choisissez la date d&apos;arrêt des données</>
            : peutFiger
              ? <><CheckCircle2 size={14} aria-hidden="true" /> Aucun bloquant — le rapport sera émis et déposé</>
              : <><AlertTriangle size={14} aria-hidden="true" /> Résolvez les bloquants ci-dessus</>}
        </span>
      </div>
    </div>
  );
}
